import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, Search, Edit, Trash2, Package, X, RefreshCw, Download,
  ArrowUpDown, ArrowUp, ArrowDown, DollarSign, TrendingUp
} from "lucide-react";
import axios from "axios";

const UNITS = [
  { value: "st", label: "Styck" },
  { value: "timmar", label: "Timmar" },
  { value: "m", label: "Meter" },
  { value: "m2", label: "Kvadratmeter" },
  { value: "kg", label: "Kilogram" },
  { value: "l", label: "Liter" },
  { value: "paket", label: "Paket" },
];

const VAT_RATES = [
  { value: 25, label: "25%" },
  { value: 12, label: "12%" },
  { value: 6, label: "6%" },
  { value: 0, label: "0% (Momsfri)" },
];

const Articles = () => {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Multi-select
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    article_number: "",
    name: "",
  });
  
  const [formData, setFormData] = useState({
    article_number: "",
    name: "",
    description: "",
    unit: "st",
    price: "",
    vat_rate: 25,
    account_number: "3010"
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Kunde inte hämta artiklar");
    } finally {
      setLoading(false);
    }
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedArticles.size === filteredArticles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(filteredArticles.map(a => a.id)));
    }
  };

  const toggleArticle = (id) => {
    const newSelected = new Set(selectedArticles);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedArticles(newSelected);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedArticles.size === 0) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const id of selectedArticles) {
      try {
        await axios.delete(`${API}/articles/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch {
        failed++;
      }
    }
    
    if (deleted > 0) toast.success(`${deleted} artiklar borttagna`);
    if (failed > 0) toast.error(`${failed} kunde inte tas bort`);
    
    setSelectedArticles(new Set());
    fetchArticles();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price) };
      
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Artikel uppdaterad");
      } else {
        await axios.post(`${API}/articles`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Artikel skapad");
      }
      fetchArticles();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ett fel uppstod");
    }
  };

  const handleDelete = async (article) => {
    try {
      await axios.delete(`${API}/articles/${article.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Artikel borttagen");
      fetchArticles();
    } catch (error) {
      toast.error("Kunde inte ta bort artikel");
    }
  };

  const openEditDialog = (article) => {
    setEditingArticle(article);
    setFormData({
      article_number: article.article_number || "",
      name: article.name || "",
      description: article.description || "",
      unit: article.unit || "st",
      price: article.price?.toString() || "",
      vat_rate: article.vat_rate || 25,
      account_number: article.account_number || "3010"
    });
    setDialogOpen(true);
  };

  const resetForm = async () => {
    setEditingArticle(null);
    
    let nextNumber = "";
    try {
      const response = await axios.get(`${API}/articles/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      nextNumber = response.data.next_number;
    } catch (error) {
      console.error("Error fetching next article number:", error);
    }
    
    setFormData({
      article_number: nextNumber,
      name: "",
      description: "",
      unit: "st",
      price: "",
      vat_rate: 25,
      account_number: "3010"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Filtering and sorting
  const filteredArticles = useMemo(() => {
    return articles
      .filter(a => {
        const matchesSearch = !searchTerm ||
          a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.article_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesArticleNumber = !columnFilters.article_number ||
          a.article_number?.toLowerCase().includes(columnFilters.article_number.toLowerCase());
        const matchesName = !columnFilters.name ||
          a.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
        
        return matchesSearch && matchesArticleNumber && matchesName;
      })
      .sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        if (sortColumn === "price") {
          aVal = a.price || 0;
          bVal = b.price || 0;
        } else if (typeof aVal === 'string') {
          aVal = aVal?.toLowerCase() || "";
          bVal = bVal?.toLowerCase() || "";
        }
        
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [articles, searchTerm, columnFilters, sortColumn, sortDirection]);

  // Statistics
  const stats = useMemo(() => {
    const total = articles.length;
    const avgPrice = total > 0 ? articles.reduce((s, a) => s + (a.price || 0), 0) / total : 0;
    const highestPrice = articles.length > 0 ? Math.max(...articles.map(a => a.price || 0)) : 0;
    const services = articles.filter(a => a.unit === "timmar").length;
    const products = articles.filter(a => a.unit !== "timmar").length;
    
    return { total, avgPrice, highestPrice, services, products };
  }, [articles]);

  // Export
  const exportArticles = () => {
    const csv = [
      ["Artikelnr", "Namn", "Beskrivning", "Enhet", "Pris", "Moms", "Konto"].join(";"),
      ...filteredArticles.map(a => [
        a.article_number,
        a.name,
        a.description,
        a.unit,
        a.price,
        a.vat_rate + "%",
        a.account_number
      ].join(";"))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `artiklar_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Export klar!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="articles">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Artiklar</h1>
          <p className="text-slate-600">Hantera dina produkter och tjänster</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportArticles}>
            <Download size={16} className="mr-2" />
            Exportera
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-article-btn">
                <Plus size={16} className="mr-2" />
                Ny artikel
              </Button>
            </DialogTrigger>
            <DialogContent size="lg">
              <DialogHeader>
                <DialogTitle>{editingArticle ? "Redigera artikel" : "Ny artikel"}</DialogTitle>
                <DialogDescription>
                  {editingArticle ? "Uppdatera artikelinformation" : "Skapa en ny artikel/tjänst"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="article_number">Artikelnummer</Label>
                    <Input
                      id="article_number"
                      value={formData.article_number}
                      onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
                      placeholder="ART001"
                      data-testid="article-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit">Enhet</Label>
                    <Select
                      value={formData.unit}
                      onValueChange={(value) => setFormData({ ...formData, unit: value })}
                    >
                      <SelectTrigger data-testid="article-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Namn *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Konsulttjänst"
                    required
                    data-testid="article-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beskrivning</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Beskrivning av artikel/tjänst"
                    data-testid="article-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Pris (exkl. moms) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="1000.00"
                      required
                      data-testid="article-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_rate">Momssats</Label>
                    <Select
                      value={formData.vat_rate.toString()}
                      onValueChange={(value) => setFormData({ ...formData, vat_rate: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="article-vat">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map((rate) => (
                          <SelectItem key={rate.value} value={rate.value.toString()}>
                            {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_number">Försäljningskonto</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder="3010"
                    data-testid="article-account"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Avbryt
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-article-btn">
                    {editingArticle ? "Uppdatera" : "Skapa"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Package className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Totalt artiklar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{formatCurrency(stats.avgPrice)}</p>
                <p className="text-xs text-slate-500">Snittpris</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-purple-600">{formatCurrency(stats.highestPrice)}</p>
                <p className="text-xs text-slate-500">Högsta pris</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Package className="text-blue-600" size={16} />
              <div>
                <p className="text-xl font-bold text-blue-600">{stats.products}</p>
                <p className="text-xs text-slate-500">Produkter</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Package className="text-amber-600" size={16} />
              <div>
                <p className="text-xl font-bold text-amber-600">{stats.services}</p>
                <p className="text-xs text-slate-500">Tjänster</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedArticles.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedArticles.size} artiklar valda
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedArticles(new Set())}>
                  Avmarkera
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 border-red-300"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={14} className="mr-1" />
                  Ta bort valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Sök artiklar (namn, artikelnr, beskrivning)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-articles"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setColumnFilters({ article_number: "", name: "" });
              }}
            >
              <X size={14} className="mr-1" />
              Rensa filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Articles Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Artikellista ({filteredArticles.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredArticles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedArticles.size === filteredArticles.length && filteredArticles.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("article_number")}>
                        Artikelnr {getSortIcon("article_number")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("name")}>
                        Namn {getSortIcon("name")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("unit")}>
                        Enhet {getSortIcon("unit")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("price")}>
                        Pris {getSortIcon("price")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("vat_rate")}>
                        Moms {getSortIcon("vat_rate")}
                      </button>
                    </th>
                    <th className="text-right p-3 font-medium">Åtgärder</th>
                  </tr>
                  {/* Column Filters */}
                  <tr className="bg-slate-100/50">
                    <th className="p-1"></th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.article_number}
                        onChange={(e) => setColumnFilters({...columnFilters, article_number: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.name}
                        onChange={(e) => setColumnFilters({...columnFilters, name: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.map((article) => (
                    <tr 
                      key={article.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50 ${selectedArticles.has(article.id) ? 'bg-indigo-50' : ''}`}
                      data-testid={`article-row-${article.id}`}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedArticles.has(article.id)}
                          onCheckedChange={() => toggleArticle(article.id)}
                        />
                      </td>
                      <td className="p-3 font-mono text-sm">{article.article_number || "-"}</td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{article.name}</p>
                          {article.description && (
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">{article.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-slate-600">{UNITS.find(u => u.value === article.unit)?.label || article.unit}</td>
                      <td className="p-3 text-right font-medium">{formatCurrency(article.price)}</td>
                      <td className="p-3 text-right text-slate-600">{article.vat_rate}%</td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(article)} data-testid={`edit-article-${article.id}`}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(article)} className="text-red-500" data-testid={`delete-article-${article.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <Package className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga artiklar hittades</p>
              <p className="text-sm mt-1">Klicka på "Ny artikel" för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Articles;
